"use client";
import { useState, useEffect } from "react";
import {
  X,
  GraduationCap,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import CustomSelect from "../common/CustomSelect";

const statusOptions = [
  {
    value: "pending",
    label: "Pending",
    icon: Clock,
    color: "text-amber-600 bg-amber-50 border-amber-200",
  },
  {
    value: "approved",
    label: "Approved",
    icon: CheckCircle,
    color: "text-green bg-green-50 border-green",
  },
  {
    value: "rejected",
    label: "Rejected",
    icon: XCircle,
    color: "text-red bg-red-50 border-red",
  },
];

const EditEducationModal = ({
  isOpen,
  onRequestClose,
  onSave,
  education,
  degrees = [],
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    degree: null,
    course: "",
    university: "",
    place: "",
    website: "",
    status: statusOptions[0], // Set to first option instead of entire array
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (education) {
      const selectedDegree =
        degrees.find((deg) => deg.value === education.degree?._id) || null;

      const selectedStatus =
        statusOptions.find((s) => s.value === education.status) ||
        statusOptions[0];

      setFormData({
        degree: selectedDegree,
        course: education.course || "",
        university: education.university || "",
        place: education.place || "",
        website: education.website || "",
        status: selectedStatus,
        isActive: education.isActive !== undefined ? education.isActive : true,
      });
      setErrors({});
    }
  }, [education, degrees]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.degree) newErrors.degree = "Degree is required";
    if (!formData.course.trim()) newErrors.course = "Course is required";
    if (!formData.university.trim())
      newErrors.university = "University is required";
    if (!formData.place.trim()) newErrors.place = "Location is required";

    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website =
        "Please enter a valid URL (include http:// or https://)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleInputChange = (nameOrEvent, value) => {
    let name, newValue;

    if (typeof nameOrEvent === "string") {
      name = nameOrEvent;
      newValue = value;
    } else {
      const {
        name: eventName,
        value: eventValue,
        type,
        checked,
      } = nameOrEvent.target;
      name = eventName;
      newValue = type === "checkbox" ? checked : eventValue;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSave(education._id, {
        ...formData,
        degree: formData.degree?.value || "",
        status: formData.status?.value || "pending",
      });
    }
  };

  const handleClose = () => {
    setFormData({
      degree: null,
      course: "",
      university: "",
      place: "",
      website: "",
      status: statusOptions[0],
      isActive: true,
    });
    setErrors({});
    onRequestClose();
  };

  if (!education) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-auto relative outline-none max-h-[95vh] overflow-hidden">
        <div className="bg-gradient-to-r from-[#4f22d8] to-[#6366f1] px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <GraduationCap size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Edit Education
                </h2>
                <p className="text-blue-100 text-sm">
                  Update your education details
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/20 transition-all duration-200"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(95vh-120px)]">
          <div className="p-8">
            {/* Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
              {/* Degree */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Degree <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <CustomSelect
                    value={formData.degree}
                    onChange={(val) => handleInputChange("degree", val)}
                    options={degrees}
                    placeholder="Select your degree"
                    required
                  />
                </div>
                {errors.degree && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle size={14} />
                    <p className="text-xs font-medium">{errors.degree}</p>
                  </div>
                )}
              </div>

              {/* Course */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Course/Specialization <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="course"
                  value={formData.course}
                  onChange={handleInputChange}
                  placeholder="e.g., Computer Science, Business Administration"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-[#4f22d8]/20 focus:border-[#4f22d8] transition-all duration-200 text-sm bg-gray-50 focus:bg-white ${
                    errors.course
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  disabled={isLoading}
                />
                {errors.course && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle size={14} />
                    <p className="text-xs font-medium">{errors.course}</p>
                  </div>
                )}
              </div>

              {/* University */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  University/Institution <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="university"
                  value={formData.university}
                  onChange={handleInputChange}
                  placeholder="e.g., Harvard University, MIT"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-[#4f22d8]/20 focus:border-[#4f22d8] transition-all duration-200 text-sm bg-gray-50 focus:bg-white ${
                    errors.university
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  disabled={isLoading}
                />
                {errors.university && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle size={14} />
                    <p className="text-xs font-medium">{errors.university}</p>
                  </div>
                )}
              </div>

              {/* Location */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="place"
                  value={formData.place}
                  onChange={handleInputChange}
                  placeholder="e.g., Boston, MA, USA"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-[#4f22d8]/20 focus:border-[#4f22d8] transition-all duration-200 text-sm bg-gray-50 focus:bg-white ${
                    errors.place
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  disabled={isLoading}
                />
                {errors.place && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle size={14} />
                    <p className="text-xs font-medium">{errors.place}</p>
                  </div>
                )}
              </div>

              {/* Website */}
              <div className="lg:col-span-2 space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  University Website{" "}
                  <span className="text-gray-500 font-normal text-xs ml-1">
                    (Optional)
                  </span>
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://www.university.edu"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-[#4f22d8]/20 focus:border-[#4f22d8] transition-all duration-200 text-sm bg-gray-50 focus:bg-white ${
                    errors.website
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  disabled={isLoading}
                />
                {errors.website && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle size={14} />
                    <p className="text-xs font-medium">{errors.website}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="mt-8 space-y-2">
              <label className="block text-sm font-semibold text-gray-800">
                Status
              </label>
              <div className="flex-1 max-w-xs">
                <CustomSelect
                  value={formData.status}
                  onChange={(val) => handleInputChange("status", val)}
                  options={statusOptions}
                  placeholder="Select status"
                  required
                />
              </div>
            </div>

            {/* Active toggle */}
            <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">
                    Visibility Status
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Control whether this education entry is displayed in your
                    profile
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={`text-sm font-medium ${
                      formData.isActive ? "text-green" : "text-gray-500"
                    }`}
                  >
                    {formData.isActive ? "Visible" : "Hidden"}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="sr-only"
                      disabled={isLoading}
                    />
                    <div
                      className={`relative w-14 h-7 rounded-full transition-all duration-300 shadow-inner ${
                        formData.isActive
                          ? "bg-gradient-to-r from-[#4f22d8] to-[#6366f1] shadow-[#4f22d8]/30"
                          : "bg-gray-300 group-hover:bg-gray-400"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-lg transform transition-all duration-300 flex items-center justify-center ${
                          formData.isActive ? "translate-x-7" : "translate-x-0"
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                            formData.isActive ? "bg-[#4f22d8]" : "bg-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 pt-8 border-t border-gray-200 mt-8">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:ring-4 focus:ring-gray-200"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-8 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#4f22d8] to-[#6366f1] rounded-xl hover:from-[#4318c7] hover:to-[#5855eb] transition-all duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl focus:ring-4 focus:ring-[#4f22d8]/30 transform hover:scale-[1.02]"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEducationModal;
